import type { PlacedStudy } from '../../features/collections/api';

/** What the viewer's previous/next needs of a study card: its id, its name and its band. */
export type Placed = Pick<PlacedStudy, 'public_id' | 'study_id' | 'description' | 'group_public_id'>;

/**
 * A collection's studies in the order its page lists them (CollectionPage and
 * CollectionView alike): each group's band in the groups' order, then the
 * ungrouped band; inside a band, the order the server placed them in. A study
 * filed under a group the page does not draw is not on the page, so it is not
 * here either.
 */
export function inPageOrder<S extends Pick<Placed, 'group_public_id'>>(groups: ReadonlyArray<{ public_id: string }>, studies: readonly S[]): S[] {
  return [...groups.map((g) => studies.filter((s) => s.group_public_id === g.public_id)), studies.filter((s) => !s.group_public_id)].flat();
}

export interface Neighbours<S> {
  /** Zero-based place of the current study in `order`. */
  index: number;
  total: number;
  prev?: S;
  next?: S;
}

/** The studies either side of `id` in `order`; null when `id` is not listed there. */
export function neighboursOf<S extends { public_id: string }>(order: readonly S[], id: string): Neighbours<S> | null {
  const index = order.findIndex((s) => s.public_id === id);
  if (index < 0) return null;
  return { index, total: order.length, prev: order[index - 1], next: order[index + 1] };
}
